import { HttpClient, HttpContext, httpResource, HttpResourceRef } from "@angular/common/http";
import { inject, Injectable, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { CACHE_CONFIG, QueryOptions } from "@kno2/core/http";
import { filterNonEmptyProperties } from "@kno2/utils";
import { firstValueFrom, map, Observable } from "rxjs";
import { AdminSubscriptionPlan, SubscriptionPlanPagedResult } from "./subscription-plans.models";

export interface SubscriptionPlan {
    orgId: number;
    planId: number;
    isFreePlan: boolean;
    freeUrl: string;
    getInTouchUrl: string;
    totalUsersCount: number;
    totalAddressesCount: number;
    isUnlimittedUsers: boolean;
    isUsersLimitReached: boolean;
    allowedUsers: number;
    allowedUsersText: string;
    isUnlimittedAddresses: boolean;
    isAddressesLimitReached: boolean;
    allowedAddresses: number;
    allowedAddressesText: string;
    upgradePlanId?: number;
    upgradePlanAllowedUsers?: number;
    downgradePlanId: number;
    downgradePlanAllowedUsers: number;
    unlimitedUsersDescription: string;
}

@Injectable({
    providedIn: "root"
})
export class SubscriptionPlansService {
    private readonly httpClient = inject(HttpClient);

    public getSubscriptionPlan(organizationId?: string): Signal<SubscriptionPlan> {
        return toSignal(this.getSubscriptionPlan$(organizationId));
    }

    public getSubscriptionPlanAsync(organizationId?: string): Promise<SubscriptionPlan> {
        return firstValueFrom(this.getSubscriptionPlan$(organizationId));
    }

    public getSubscriptionPlans = (params: Signal<QueryOptions>): HttpResourceRef<SubscriptionPlanPagedResult> =>
        httpResource(() => ({
            url: "/api/subscriptionplans",
            params: filterNonEmptyProperties({ ...params() }),
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true })
        }));

    public getSubscriptionPlanById = (id: AdminSubscriptionPlan["id"]): Promise<AdminSubscriptionPlan> =>
        firstValueFrom(this.httpClient.get<AdminSubscriptionPlan>(`/api/subscriptionplans/${id}`));

    public createSubscriptionPlan = (plan: AdminSubscriptionPlan): Promise<AdminSubscriptionPlan> =>
        firstValueFrom(
            this.httpClient.post<AdminSubscriptionPlan>("/api/subscriptionplans", plan, {
                context: new HttpContext().set(CACHE_CONFIG, { invalidateCache: "/api/subscriptionplans" })
            })
        );

    public updateSubscriptionPlan = (plan: AdminSubscriptionPlan): Promise<AdminSubscriptionPlan> =>
        firstValueFrom(
            this.httpClient.put<AdminSubscriptionPlan>(`/api/subscriptionplans/${plan.id}`, plan, {
                context: new HttpContext().set(CACHE_CONFIG, { invalidateCache: "/api/subscriptionplans" })
            })
        );

    public deleteSubscriptionPlan = (plan: AdminSubscriptionPlan): Promise<void> =>
        firstValueFrom(
            this.httpClient.delete<void>(`/api/subscriptionplans/${plan.id}`, {
                context: new HttpContext().set(CACHE_CONFIG, { invalidateCache: "/api/subscriptionplans" })
            })
        );

    public getSubscriptionPlansAsync = (): Promise<SubscriptionPlanPagedResult> => firstValueFrom(this.getSubscriptionPlans$());

    public getAllSubscriptionPlans = (): Signal<AdminSubscriptionPlan[]> =>
        toSignal(this.getSubscriptionPlans$().pipe(map((item) => item.items)), {
            initialValue: []
        });

    private getSubscriptionPlan$(organizationId?: string): Observable<SubscriptionPlan> {
        return this.httpClient.get<SubscriptionPlan>(`/api/subscriptionplans/subscriptions`, {
            params: {
                ...filterNonEmptyProperties({ organizationId })
            },
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true })
        });
    }

    private getSubscriptionPlans$(): Observable<SubscriptionPlanPagedResult> {
        return this.httpClient.get<SubscriptionPlanPagedResult>("/api/subscriptionplans", {
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true })
        });
    }
}
